/**
 * The player page header (Scores UX spec v1.0 section 9): no card, a 72px
 * round headshot (the stored one, or the player's initials, player-profile
 * rule 5), 16, then the player's name as the page's `h1` on up to two lines
 * (never truncated) over "Ravens · QB · #8" in `caption` `textSecondary`,
 * every empty part dropped. The team part is a link into that team's page.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";

import { PlayerHeadshot } from "./PlayerHeadshot";
import type { typePlayerProfileView } from "./playerProfileReader";
import {
  initialsFromPersonName,
  playerInjuryStatusLabel,
  playerMetaParts,
} from "./playerProfileView";
import { PLAYER_NO_DETAILS_LABEL } from "./playerProfileCopy";

export type typePlayerProfileHeaderProps = {
  view: typePlayerProfileView;
  /** The stored headshot's media URL from /team/roster-get, or null. Null shows the initials. */
  headshotUrl: string | null;
};

export const PlayerProfileHeader = (
  props: typePlayerProfileHeaderProps,
): ReactNode => {
  const { view } = props;
  const injuryLabel = playerInjuryStatusLabel(view.injuryStatus);
  const { team, rest } = playerMetaParts({
    teamName: view.teamName,
    jerseyNumber: view.jerseyNumber,
    position: view.position,
  });
  const parts: ReactNode[] = [];
  if (team !== null) {
    parts.push(
      view.teamId !== null ? (
        <Link
          key="team"
          to="/scores"
          search={{ teamId: view.teamId }}
          data-testid="player-meta-team-link"
          className={cn(
            "text-primary no-underline hover:underline",
            FOCUS_RING,
          )}
        >
          {team}
        </Link>
      ) : (
        <span key="team">{team}</span>
      ),
    );
  }
  for (const part of rest) parts.push(<span key={part}>{part}</span>);

  return (
    <div data-testid="player-header" className="flex items-center gap-4 pb-2">
      <PlayerHeadshot
        headshotUrl={props.headshotUrl}
        size="xl"
        fallbackInitials={initialsFromPersonName(view.name)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h1
          className="text-h1 wrap-break-word text-ink"
          data-testid="player-name"
        >
          {view.name}
        </h1>
        <p
          className="text-caption text-ink-secondary"
          data-testid="player-meta"
        >
          {parts.length === 0
            ? PLAYER_NO_DETAILS_LABEL
            : parts.map((part, index) => (
                <Fragment key={index}>
                  {index > 0 ? " · " : null}
                  {part}
                </Fragment>
              ))}
        </p>
        {injuryLabel !== null ? (
          <span className="text-caption text-error" data-testid="player-injury">
            {injuryLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};
